import { useEffect, useState } from 'react';
import { Alert, Platform, Pressable, Switch, Text, View } from 'react-native';
import { useAuth } from '../../auth/AuthContext';
import { traduzErro } from '../../lib/supabase';
import { ActionSheet, Field, Section, Sheet, type Action } from '../../components/ui';
import { C } from '../../components/theme';
import { Padrao } from '../../components/Padrao';
import { t } from '../../lib/i18n';
import { Card, Linha } from './comum';
import { useCofreSheet } from '../../components/CofreSheet';
import { fecharCofre, useCofre } from '../../lib/cofre';
import { setDoc, useDoc } from '../../store/store';
import { Modal } from 'react-native';
import { ConfirmarCelular } from '../../components/ConfirmarCelular';
import { conferirCelular, useCelular } from '../../lib/celular';
import { useApagarConta } from './MeusDados';
import { OPCOES_BLOQUEIO, definirTempoBloqueio, tempoBloqueio } from '../../lib/computador';
import { BotaoAcao, Bloco, Dica, Divisor, LinhaEscolha, LinhaOpcoes } from '../../components/ajustes/Linhas';
import { AlvoGuia } from '../../components/guia/Destaque';
import { useAlvoGuia } from '../../lib/guia/destaque';

const WEB = Platform.OS === 'web';
const emailOk = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e.trim());

/** Folha para criar o desenho padrão: desenhar e repetir para confirmar. */
function CriarPadrao({ visible, cor, onClose, onPronto }: { visible: boolean; cor: string; onClose: () => void; onPronto: (seq: string) => void }) {
  const [primeiro, setPrimeiro] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ txt: string; erro?: boolean } | null>(null);
  const [aberto, setAberto] = useState(visible);
  if (visible !== aberto) { setAberto(visible); if (visible) { setPrimeiro(null); setMsg(null); } }

  const fim = (seq: string) => {
    if (seq.split('-').length < 4) { setMsg({ txt: t('Ligue pelo menos 4 pontos.'), erro: true }); return; }
    if (!primeiro) { setPrimeiro(seq); setMsg({ txt: t('Desenhe de novo para confirmar.') }); return; }
    if (seq !== primeiro) { setPrimeiro(null); setMsg({ txt: t('Os desenhos não conferem. Comece de novo.'), erro: true }); return; }
    onPronto(seq);
  };
  return (
    <Sheet visible={visible} onClose={onClose} title={t('Desenho padrão')}>
      <View style={{ padding: 24, gap: 18, alignItems: 'center' }}>
        <Text style={{ color: msg?.erro ? C.danger : C.text, fontSize: 16, textAlign: 'center' }}>
          {msg?.txt ?? t('Desenhe um padrão ligando pelo menos 4 pontos.')}
        </Text>
        <Padrao cor={cor} erro={msg?.erro} onFim={fim} />
        <Text style={{ color: C.sub, fontSize: 12, textAlign: 'center' }}>{t('O desenho fica guardado só neste dispositivo, protegido. A senha continua valendo se você esquecer o desenho.')}</Text>
      </View>
    </Sheet>
  );
}

/** Folha para criar o PIN (0.66, B5): 4 a 6 números, digitado duas vezes. */
export function CriarPin({ visible, cor, onClose, onPronto }: { visible: boolean; cor: string; onClose: () => void; onPronto: (pin: string) => void }) {
  const [p1, setP1] = useState(''); const [p2, setP2] = useState('');
  const [aberto, setAberto] = useState(visible);
  if (visible !== aberto) { setAberto(visible); if (visible) { setP1(''); setP2(''); } }
  const so = (v: string) => v.replace(/\D/g, '').slice(0, 6);
  const ok = () => {
    if (p1.length < 4) return Alert.alert(t('O PIN precisa ter de 4 a 6 números.'));
    if (p1 !== p2) return Alert.alert(t('Os PINs não conferem.'));
    onPronto(p1);
  };
  return (
    <Sheet visible={visible} onClose={onClose} title={t('PIN')} right={<Pressable onPress={ok}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      <View style={{ padding: 20, gap: 12 }}>
        <Field value={p1} onChangeText={v => setP1(so(v))} placeholder={t('PIN (4 a 6 números)')} secureTextEntry keyboardType="number-pad" maxLength={6} autoFocus />
        <Field value={p2} onChangeText={v => setP2(so(v))} placeholder={t('Repita o PIN')} secureTextEntry keyboardType="number-pad" maxLength={6} onSubmitEditing={ok} />
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('O PIN fica guardado só neste dispositivo, protegido. A senha continua valendo se você esquecer o PIN.')}</Text>
      </View>
    </Sheet>
  );
}

/** Ajustes > Segurança: senha, e-mail, formas de desbloquear e apagar a conta. */
export default function Seguranca({ cor, onPrivacidade }: { cor: string; onPrivacidade?: () => void }) {
  const a = useAuth();
  const alvoBio = useAlvoGuia('ajustes.seguranca.biometria');   // guia (texto 08, C1): a linha 👆 Biometria
  const aj = useDoc('ajustes');
  const prazo = aj.bloqueioMin ?? 1;
  const [abrirSenha, setAbrirSenha] = useState(false);
  const [atual, setAtual] = useState(''); const [nova, setNova] = useState(''); const [repete, setRepete] = useState('');
  const [email, setEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [criar, setCriar] = useState(false);
  const [criarPin, setCriarPin] = useState(false);
  useEffect(() => { a.avisarRosto(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const cofre = useCofre();
  const cofreSheet = useCofreSheet(cor);
  const conta = useApagarConta();   // 0.67: o mesmo de Ajustes > Meus dados (confirmação e senha)

  async function salvarSenha() {
    if (nova.length < 8) return Alert.alert(t('A senha precisa ter 8 caracteres ou mais.'));
    if (nova !== repete) return Alert.alert(t('As senhas novas não conferem.'));
    setBusy(true);
    try {
      await a.trocarSenha(atual, nova);
      setAtual(''); setNova(''); setRepete(''); setAbrirSenha(false);
      Alert.alert(t('Senha alterada.'), t('Use a senha nova da próxima vez que entrar, aqui e no My Planner Web.'));
    } catch (e) { Alert.alert(t('Não foi possível'), traduzErro(e)); }
    finally { setBusy(false); }
  }

  async function salvarEmail() {
    const e = (email ?? '').trim();
    if (e && !emailOk(e)) return Alert.alert(t('E-mail inválido'), t('Confira o e-mail ou deixe em branco.'));
    setBusy(true);
    try { await a.trocarEmail(e); setEmail(null); Alert.alert(t('E-mail salvo.')); }
    catch (err) { Alert.alert(t('Não foi possível'), traduzErro(err)); }
    finally { setBusy(false); }
  }

  const tipos = a.bioTipos.map(x => x === 'digital' ? t('digital') : x === 'rosto' ? t('reconhecimento facial') : t('íris'));
  const rotuloBio = tipos.length ? t('Biometria ({tipos})', { tipos: tipos.join(', ') }) : t('Biometria (digital ou rosto)');

  // 0.67 (texto 05 D2): quem tinha 15 ou 30 minutos continua com o valor até escolher outro.
  const prazos: [number, string][] = [0, 1, 5].map(m => [m, m === 0 ? t('Na hora') : m === 1 ? t('Depois de 1 minuto') : t('Depois de {n} minutos', { n: m })]);
  if (!prazos.some(o => o[0] === prazo)) prazos.push([prazo, t('Depois de {n} minutos', { n: prazo })]);
  const [menuDesb, setMenuDesb] = useState<Action[] | null>(null);
  const formaAtual = a.padrao ? '✍️ ' + t('Desenho padrão') : a.pin ? '🔢 ' + t('PIN (só números, 4 a 6)') : '🔑 ' + t('Senha (letras e números)');
  const abrirDesbloqueio = () => {
    const so = !a.padrao && !a.pin;
    setMenuDesb([
      { label: (so ? '✓ ' : '    ') + '🔑 ' + t('Senha (letras e números)'), onPress: () => { if (a.padrao || a.pin) Alert.alert(t('Voltar a usar só a senha?'), t('O app volta a pedir a senha para desbloquear.'), [
        { text: t('Cancelar'), style: 'cancel' }, { text: t('Usar a senha'), onPress: () => { a.setPadrao(null); a.setPin(null); } }]); } },
      { label: (a.pin ? '✓ ' : '    ') + '🔢 ' + t('PIN (só números, 4 a 6)'), onPress: () => setCriarPin(true) },
      { label: (a.padrao ? '✓ ' : '    ') + '✍️ ' + t('Desenho padrão'), onPress: () => setCriar(true) },
    ]);
  };
  const cancelarSenha = () => { setAbrirSenha(false); setAtual(''); setNova(''); setRepete(''); };

  return (<>
    <Section t={t('Senha')} />
    <Card>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('A senha é a mesma no celular e no My Planner Web.')}</Text>
      {!abrirSenha ? <BotaoAcao cor={cor} label={'🔑 ' + t('Mudar senha')} onPress={() => setAbrirSenha(true)} /> : (<>
        <Field value={atual} onChangeText={setAtual} placeholder={t('Senha atual')} secureTextEntry autoCapitalize="none" />
        <Field value={nova} onChangeText={setNova} placeholder={t('Senha nova (8 caracteres ou mais)')} secureTextEntry autoCapitalize="none" />
        <Field value={repete} onChangeText={setRepete} placeholder={t('Repita a senha nova')} secureTextEntry autoCapitalize="none" onSubmitEditing={salvarSenha} />
        <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
          <BotaoAcao cor={C.sub} label={t('Cancelar')} onPress={cancelarSenha} />
          <BotaoAcao cheio cor={cor} label={busy ? t('Aguarde...') : t('Salvar')} disabled={busy || !atual || !nova || !repete} onPress={salvarSenha} />
        </View>
      </>)}
    </Card>

    <CelularConfirmado />
    <Section t={t('E-mail de contato')} />
    <Card>
      <Text style={{ color: C.text }}>{a.email || t('Nenhum e-mail cadastrado')}</Text>
      {email === null ? <BotaoAcao cor={cor} label={'✉️ ' + t('Mudar e-mail')} onPress={() => setEmail(a.email ?? '')} /> : (<>
        <Field value={email} onChangeText={setEmail} placeholder={t('Novo e-mail')} keyboardType="email-address" autoCapitalize="none" autoCorrect={false} autoComplete="email" />
        <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
          <BotaoAcao cor={C.sub} label={t('Cancelar')} onPress={() => setEmail(null)} />
          <BotaoAcao cheio cor={cor} label={busy ? t('Aguarde...') : t('Salvar')} disabled={busy} onPress={salvarEmail} />
        </View>
      </>)}
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('O acesso continua sendo pelo CPF. O e-mail serve para o suporte falar com você.')}</Text>
    </Card>

    {WEB && <BloqueioComputador />}
    {!WEB && (<>
      <Bloco titulo={t('Desbloqueio do app')}>
        <LinhaOpcoes icone="⏱️" titulo={t('Bloquear o app')} valor={prazo} opcoes={prazos}
          onEscolher={m => setDoc('ajustes', d => ({ ...d, bloqueioMin: m }))}
          dica={t('O tempo conta a partir do momento em que o app sai da tela (outro app, tela inicial ou tela apagada). Puxar as notificações, a janela da biometria, um pedido de permissão ou escolher uma foto ou arquivo não contam.')} />
        <Divisor />
        <AlvoGuia id="ajustes.seguranca.desbloqueio"><LinhaEscolha icone="🔓" titulo={t('Desbloquear com')} valor={formaAtual} onPress={abrirDesbloqueio} /></AlvoGuia>
        {a.padrao && (<><Divisor /><LinhaEscolha icone="✍️" titulo={t('Trocar o desenho')} onPress={() => setCriar(true)} /></>)}
        {a.pin && (<><Divisor /><LinhaEscolha icone="🔢" titulo={t('Trocar o PIN')} onPress={() => setCriarPin(true)} /></>)}
        <Divisor />
        <View ref={alvoBio.ref} collapsable={false} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6, opacity: a.bioDisponivel ? 1 : 0.6 }}>
          <Text style={{ fontSize: 18 }}>👆</Text>
          <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{rotuloBio}</Text>
          <Switch value={a.bio} disabled={!a.bioDisponivel} trackColor={{ true: cor }}
            onValueChange={async v => { if (!(await a.setBio(v))) Alert.alert(t('Biometria não confirmada.')); }} />
        </View>
        {!a.bioDisponivel && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Cadastre digital, rosto ou um código de bloqueio no dispositivo para usar.')}</Text>}
        <Dica>{t('Com a biometria ligada, ela aparece primeiro. Se falhar, a mesma janela oferece o código/PIN/padrão do dispositivo; a senha do app continua valendo.')}</Dica>
        <BotaoAcao cor={cor} label={'🔒 ' + t('Bloquear agora')} onPress={a.lock} />
      </Bloco>
      <ActionSheet title={t('Desbloquear com')} actions={menuDesb} onClose={() => setMenuDesb(null)} />
      <CriarPadrao visible={criar} cor={cor} onClose={() => setCriar(false)}
        onPronto={async seq => { await a.setPin(null); await a.setPadrao(seq); setCriar(false); Alert.alert(t('Desenho padrão salvo.')); }} />
      <CriarPin visible={criarPin} cor={cor} onClose={() => setCriarPin(false)}
        onPronto={async p => { await a.setPadrao(null); await a.setPin(p); setCriarPin(false); Alert.alert(t('PIN salvo.')); }} />
    </>)}

    <Section t={t('Cofre (criptografia de ponta a ponta)')} />
    <Card>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Guarda documentos, exames e histórico de saúde com uma senha só dele. Nem o suporte consegue ler. Se você esquecer a senha, não há como recuperar.')}</Text>
      <Text style={{ color: C.text }}>{!cofre.configurado ? '🔓 ' + t('Cofre ainda não criado') : cofre.aberto ? '🔓 ' + t('Cofre aberto (fecha sozinho em 5 minutos sem uso)') : '🔐 ' + t('Cofre fechado')}</Text>
      {!cofre.configurado ? <BotaoAcao cheio cor={cor} label={t('Criar o cofre')} onPress={() => cofreSheet.pedir('criar')} /> : (
        <View style={{ flexDirection: 'row', columnGap: 10, flexWrap: 'wrap' }}>
          {cofre.aberto ? <BotaoAcao cor={cor} label={t('Fechar o cofre')} onPress={fecharCofre} />
            : <BotaoAcao cor={cor} label={t('Abrir o cofre')} onPress={() => cofreSheet.pedir('abrir')} />}
          <BotaoAcao cor={cor} label={t('Trocar a senha do cofre')} onPress={() => cofreSheet.pedir('trocar').then(ok => { if (ok) Alert.alert(t('Senha do cofre alterada.')); })} />
        </View>
      )}
      {cofreSheet.tela}
    </Card>

    <Bloco titulo={t('Privacidade')}>
      {!!onPrivacidade && (<><LinhaEscolha icone="📄" titulo={t('Política de privacidade, LGPD e termos')} onPress={onPrivacidade} /><Divisor /></>)}
      <LinhaEscolha icone="🗑️" cor={C.danger} titulo={t('Apagar minha conta e todos os dados')} onPress={conta.pedir} />
    </Bloco>
    {conta.tela}
  </>);
}

/** Celular confirmado pelo WhatsApp: mostra o número e deixa trocar (pede um código novo). */
function CelularConfirmado() {
  const cel = useCelular();
  const [trocar, setTrocar] = useState(false);
  useEffect(() => { if (!cel.checado) conferirCelular(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cel.checado || !cel.ligado) return null;   // WhatsApp ainda não configurado no servidor: não mostra
  return (<>
    <Section t={t('Celular confirmado')} />
    <Card>
      <Linha l={'📱 ' + (cel.telefone ?? t('Ainda não confirmado'))}>
        <Pressable onPress={() => setTrocar(true)}><Text style={{ color: '#25D366', fontWeight: '700' }}>{cel.telefone ? t('Trocar número') : t('Confirmar')}</Text></Pressable>
      </Linha>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('O celular serve só para confirmar que a conta é sua (código pelo WhatsApp no cadastro e ao trocar de número). Um número por conta.')}</Text>
    </Card>
    <Modal visible={trocar} animationType="slide" onRequestClose={() => setTrocar(false)}>
      <ConfirmarCelular titulo={t('Trocar o número do celular')} onPronto={() => setTrocar(false)} onCancelar={() => setTrocar(false)} />
    </Modal>
  </>);
}

/** Texto 12 C: no computador, bloqueio automático por tempo parado (por causa do Diário e do Prontuário). */
function BloqueioComputador() {
  const a = useAuth();
  const [min, setMin] = useState(15);
  useEffect(() => { tempoBloqueio().then(setMin); }, []);
  const opcoes: [number, string][] = OPCOES_BLOQUEIO.map(m => [m, t('Depois de {n} minutos parado', { n: m })]);
  return (
    <Bloco titulo={t('Este computador')}>
      <LinhaOpcoes icone="⏱️" titulo={t('Bloquear o computador')} valor={min} opcoes={opcoes} onEscolher={m => { setMin(m); definirTempoBloqueio(m); }}
        dica={t('Sem mexer no mouse ou no teclado por esse tempo, o My Planner bloqueia. Para voltar, digite a senha da conta (ou entre com o QR Code de novo).')} />
      <Divisor />
      <BotaoAcao cor={C.danger} label={'🚪 ' + t('Sair deste computador')} onPress={() => Alert.alert(t('Sair deste computador?'),
        t('Os seus dados continuam salvos na nuvem e no celular. Para entrar de novo, use o QR Code.'),
        [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Sair'), style: 'destructive', onPress: () => a.signOut() }])} />
    </Bloco>
  );
}
